import { FormControl, TextField } from '@mui/material';
import { DateValidationError } from '@mui/x-date-pickers/models';

import { CoreBox, CoreTypography } from '@youscience/core';
import { brightpathTheme } from '@youscience/theme';

import CoreDatePicker from '@components/CoreDatePicker';

interface MonthDayYearDatePickerProps {
  name: string;
  label: string;
  value: string | undefined;
  error?: string;
  minDate?: Date;
  maxDate?: Date;
  required?: boolean;
  slotProps?: {
    inputProps?: {
      clearable?: boolean;
      onClear?: () => void;
    };
    error?: boolean;
  };
  handleDateChange: (date: Date | null) => void;
  handleDateError?: (name: string, error: string | undefined) => void;
}

export const MonthDayYearDatePicker: React.FC<MonthDayYearDatePickerProps> = ({
  name,
  label,
  value,
  error,
  minDate,
  maxDate,
  required,
  slotProps,
  handleDateChange,
  handleDateError,
}: MonthDayYearDatePickerProps) => {
  const getDate = (value: string | undefined) => {
    if (!value) {
      return null;
    }

    try {
      const [month, day, year] = value.split('/');

      return new Date(
        parseInt(year?.padStart(4, '0'), 10),
        parseInt(month?.padStart(2, '0'), 10) - 1,
        parseInt(day?.padStart(2, '0'), 10),
      );
    } catch {
      return null;
    }
  };

  const showHelperText = () => {
    const isError = error ? { color: `${brightpathTheme.palette.error.main}` } : {};

    return (
      <CoreTypography variant="caption" sx={isError}>
        {error ? error : "Date uses 'month day year' format (e.g. 08/10/2020)"}
      </CoreTypography>
    );
  };

  return (
    <FormControl fullWidth data-testid={name}>
      <CoreDatePicker
        views={['day', 'month', 'year']}
        label={`${label} ${required ? '*' : ''}`}
        slots={{
          textField: TextField,
        }}
        slotProps={{
          textField: {
            error: slotProps?.error,
          },
        }}
        onChange={handleDateChange}
        value={getDate(value)}
        onError={(error: DateValidationError | null) => {
          handleDateError?.(name, error !== null ? error : undefined);
        }}
        minDate={minDate}
        maxDate={maxDate}
        format="MM/dd/yyyy"
        openTo="month"
      />
      <CoreBox sx={{ marginLeft: '.75rem' }}>{showHelperText()}</CoreBox>
    </FormControl>
  );
};
